import { SessionStore } from "./stores/SessionStore";
import { useApplicationStore } from "./stores/useApplicationStore";
import { createIdentityStore } from "./stores/useIdentityStore";
import { EventStore } from "./stores/EventStore";
import { ApplicationStore } from "./stores/ApplicationStore";
import { useClientStore } from "./stores/useClientStore";

import { LRUCache } from "./common/LRUCache";
import { InterceptedFetch } from "./common/InterceptedFetch";
import { createQuickProvisioningStore } from "./stores/useQuickProvisioningStore";
import { createScheduleStore } from "./stores/useScheduleStore";
import { AccessLevel } from "./model/Identity";


export interface LoadingManager {
    startLoading();
    doneLoading();
}

export interface IApplication extends LoadingManager {
    loaded: boolean;
    onLoad();
    onLoggedIn();
    onLoggedOut();
}

export interface INetworkManager {
    uriPrefix: string;
    authorization: string;
    fetch: Function;
}

export interface IDialogManager {
    showAlert(msg: string): Promise<void>;
    showConfirm(msg: string): Promise<boolean>;
}

export interface ISessionManager {
    loggedIn: boolean;
    hasAdmin: boolean;
    username: string;
    logout();
    hasAccessLevelForAnyone(accessLevel: AccessLevel): boolean;
    hasAccessLevelFor(clientId: number, accessLevel: AccessLevel): boolean;
    hasScheduleAccessForClient(clientId: number): boolean;
    hasScheduleAccessForAnyone(): boolean;
    hasScheduleAdminFor(clientId: number): boolean;
    hasPunchClockAccessFor(clientId: number): boolean;
    hasPunchClockAccessForAnyone(): boolean;
}

export interface IStoreRepository {
    sessionStore: SessionStore;
    applicationStore: ApplicationStore;

    buildClientStore(clientId?: number): any;
    buildIdentityStore(identityId: number): any;
    buildEventStore(eventId: number): any;
    buildQuickProvisioningStore(eventId: number): any;
    buildScheduleStore(eventId: number): any;
}

class Context implements IApplication, INetworkManager, IDialogManager, IStoreRepository {
    private _loaded: boolean = false;
    private _sessionStore: SessionStore = new SessionStore(() => this.onLoggedIn(), () => this.onLoggedOut());

    private _applicationStore: ApplicationStore = new ApplicationStore();

    private _identityStores: LRUCache<number, any> = new LRUCache(2);
    private _eventStores: LRUCache<number, any> = new LRUCache(2);
    private _quickProvisioningStores: LRUCache<number, any> = new LRUCache(1);
    private _scheduleStores: LRUCache<number, any> = new LRUCache(1);

    async onLoad() {
        await this._sessionStore.onLoad();
        this._loaded = true;
        if (this._sessionStore.loggedIn) {
            this.onLoggedIn();
        }
    }

    onLoggedIn() {
        this._applicationStore.refreshRegions();
        this._applicationStore.refreshClients();
        this._applicationStore.refreshEvents();
    }

    onLoggedOut() {
        this._applicationStore.clearAll();
    }

    showAlert(msg: string): Promise<void> {
        // TODO: Something prettier
        return new Promise(resolve => {
            alert(msg);
            resolve();
        });
    }

    showConfirm(msg: string): Promise<boolean> {
        // TODO: Something prettier
        return new Promise(resolve => {
            resolve(confirm(msg));
        });
    }

    buildClientStore(clientId?: number): any {
        // Return the global client store and optionally set the client ID
        const store = useClientStore.getState();
        if (clientId) {
            store.setClientId(clientId);
        } else {
            store.clearClient();
        }
        return useClientStore;
    }

    buildIdentityStore(identityId: number): any {
        if (!this._identityStores[identityId]) {
            this._identityStores[identityId] = createIdentityStore(identityId, this.applicationStore);
        }
        return this._identityStores[identityId];
    }

    buildEventStore(eventId: number): any {
        if (!this._eventStores[eventId]) {
            this._eventStores[eventId] = new EventStore(eventId, this.applicationStore);
        }
        return this._eventStores[eventId];
    }

    buildQuickProvisioningStore(eventId: number): any {
        if (!this._quickProvisioningStores[eventId]) {
            this._quickProvisioningStores[eventId] = createQuickProvisioningStore(eventId, this.applicationStore);
        }
        return this._quickProvisioningStores[eventId];
    }

    buildScheduleStore(eventId: number): any {
        if (!this._scheduleStores[eventId]) {
            this._scheduleStores[eventId] = createScheduleStore(eventId, this.applicationStore);
        }
        return this._scheduleStores[eventId];
    }

    startLoading() {
        this._applicationStore.startLoading();
    }

    doneLoading() {
        this._applicationStore.doneLoading();
    }

    isLoggedIn(): boolean {
        return this.sessionStore.loggedIn;
    }

    get loaded(): boolean {
        return this._loaded;
    }

    get sessionStore(): SessionStore {
        return this._sessionStore;
    }

    get applicationStore(): ApplicationStore {
        return this._applicationStore;
    }

    get uriPrefix(): string {
        return import.meta.env.VITE_APP_BACKEND_PREFIX;
    }

    get authorization(): string {
        return "Bearer " + this.sessionStore.tokenString;
    }

    get fetch(): Function {
        return InterceptedFetch(this.onUnauthorized);
    }

    private onUnauthorized = (): Promise<boolean> => {
        return this._sessionStore.expired();
    }

}

const context = new Context();
export default context;
export const Application: IApplication = context;
export const NetworkManager: INetworkManager = context;
export const DialogManager: IDialogManager = context;
export const SessionManager: ISessionManager = context.sessionStore;
export const StoreRepository: IStoreRepository = context;
